import { useEffect, useRef } from 'react';
import { useTranslation } from '@course-authoring/i18n';
import { useTheme } from '@course-authoring/shared-types';
import { usePrefersReducedMotion } from './hooks/usePrefersReducedMotion';

const DOT_SIZE = 10;

/** Keep the slot stable and center the indicator in the trailing whitespace. */
export function PacingBoundary({ running }: { running: boolean }): React.ReactElement {
  const { tokens } = useTheme();
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  useEffect(() => {
    if (!running || reducedMotion) return;
    const animations = Array.from(ref.current?.children ?? []).map((dot, index) =>
      typeof dot.animate === 'function'
        ? dot.animate([{ opacity: 0.25 }, { opacity: 0.75 }, { opacity: 0.25 }], {
            duration: 900, delay: index * 150, iterations: Infinity,
          })
        : null,
    );
    return () => animations.forEach((animation) => animation?.cancel());
  }, [running, reducedMotion]);
  return (
    <div data-scroll-completion-anchor data-pacing-boundary style={{ height: `calc(${tokens.spacing.xl} * 2 + ${DOT_SIZE}px)`, position: 'relative', width: '100%', transform: 'translateY(calc(var(--course-section-padding-y, 0px) / 2))' }}>
      <div role="status" aria-live="polite" aria-atomic="true" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}>
        {running ? t('player.more_content_coming') : ''}
      </div>
      <div ref={ref} aria-hidden="true" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: tokens.spacing.sm, height: '100%', visibility: running ? 'visible' : 'hidden' }}>
        {[0, 1, 2].map((dot) => <span key={dot} style={{ width: DOT_SIZE, height: DOT_SIZE, borderRadius: '50%', opacity: 0.45, backgroundColor: tokens.text.muted }} />)}
      </div>
    </div>
  );
}
